import { useEffect, useMemo, useRef, useState } from 'react';
import CreateCustomerModal from './CreateCustomerModal';
import TransactionDetailsModal from './TransactionDetailsModal';
import SalesPersonPanel, { type SelectedSalesPerson } from './SalesPersonPanel';
import { customerApi, type CustomerOption } from '../../../api/customers';
import { posApi } from '../../../api/pos';
import { unwrapResponse } from '../../../api/client';

interface SelectedCustomer {
  id: number;
  name: string;
  phoneNumber?: string | null;
  emailAddress?: string | null;
  predefinedValue?: string | null;
}

interface CustomerPanelProps {
  clearTrigger?: number;
  refetchTrigger?: number;
  selectedCustomer: SelectedCustomer | null;
  onSelectCustomer: (customer: SelectedCustomer | null) => void;
  selectedSalesPerson: SelectedSalesPerson | null;
  onSelectSalesPerson: (person: SelectedSalesPerson | null) => void;
  clearSalesPersonTrigger?: number;
}

export default function CustomerPanel({
  clearTrigger,
  refetchTrigger,
  selectedCustomer,
  onSelectCustomer,
  selectedSalesPerson,
  onSelectSalesPerson,
  clearSalesPersonTrigger,
}: CustomerPanelProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [customerList, setCustomerList] = useState<CustomerOption[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [allowWalkInCustomer, setAllowWalkInCustomer] = useState(true);
  const [recentTransactions, setRecentTransactions] = useState<any[]>([]);
  const [loadingRecentTransactions, setLoadingRecentTransactions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const loadCustomers = async (search = '') => {
    setIsLoadingCustomers(true);
    setCustomerError(null);
    try {
      const [settingsResponse, customers] = await Promise.all([
        posApi.getPosSettings(),
        customerApi.getSelectList(search, 0, 50),
      ]);
      const settings = unwrapResponse<any>(settingsResponse);
      const walkInEnabled = !!settings?.allowWalkInCustomer;
      setAllowWalkInCustomer(walkInEnabled);

      const filteredCustomers = walkInEnabled
        ? customers
        : customers.filter((customer) => customer.predefinedValue !== 'walkincustomer');
      setCustomerList(filteredCustomers);

      if (!selectedCustomer) {
        const walkInCustomer = filteredCustomers.find(
          (customer) => customer.predefinedValue === 'walkincustomer'
        );
        if (walkInCustomer) {
          onSelectCustomer(walkInCustomer);
        }
      }
      return filteredCustomers;
    } catch (error: any) {
      setCustomerError(error?.message || 'Failed to load customers.');
      return [];
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    loadRecentTransactions();
  }, []);

  useEffect(() => {
    if (refetchTrigger) loadRecentTransactions();
  }, [refetchTrigger]);

  useEffect(() => {
    if (clearTrigger && clearTrigger > 0) {
      const walkInCustomer = customerList.find(
        (customer) => customer.predefinedValue === 'walkincustomer'
      );
      onSelectCustomer(walkInCustomer ?? null);
      setCustomerSearch('');
    }
  }, [clearTrigger, customerList, onSelectCustomer]);

  const toNumber = (value: unknown, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const mapOrderToTransaction = (order: any) => {
    const orderLines = Array.isArray(order?.orderLines) ? order.orderLines : [];
    const saleDateValue =
      order?.saleDate ??
      order?.createdAt ??
      order?.createdDate ??
      order?.date ??
      null;
    const saleDate = saleDateValue ? new Date(saleDateValue) : new Date();
    const totalItems = orderLines.reduce(
      (sum: number, line: any) => sum + Math.max(1, toNumber(line?.quantity, 1)),
      0
    );
    const fallbackItemCount = toNumber(
      order?.items ??
      order?.itemCount ??
      order?.itemsCount ??
      order?.totalItems ??
      order?.totalItem ??
      order?.numberOfItems ??
      order?.lineCount,
      0
    );

    return {
      rawId: toNumber(order?.id ?? order?.orderId ?? order?.posOrderId, 0),
      id:
        order?.invoiceNumber ??
        order?.orderNumber ??
        order?.receiptNo ??
        `TXN-${toNumber(order?.id ?? order?.orderId ?? 0)}`,
      time: saleDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
      date: saleDate.toLocaleDateString('en-US'),
      amount: `${toNumber(order?.grandTotal ?? order?.total ?? order?.netAmount, 0).toFixed(2)}`,
      items: totalItems || fallbackItemCount || orderLines.length,
      cashier:
        order?.counterSession?.user?.name ??
        order?.cashierName ??
        order?.createdByUserName ??
        'N/A',
      customer:
        order?.customer?.name ??
        order?.customerName ??
        order?.partyProfile?.name ??
        undefined,
      paymentMethod:
        (Array.isArray(order?.payments) && order.payments[0]?.methodName) ||
        order?.paymentMethodName ||
        'N/A',
      paymentMethods: Array.isArray(order?.payments) && order.payments.length > 0
        ? order.payments.map((p: any) => ({ method: p?.methodName ?? p?.paymentMethodName ?? 'Payment', amount: toNumber(p?.amount ?? p?.paidAmount, 0) }))
        : [{ method: order?.paymentMethodName ?? 'Payment', amount: toNumber(order?.grandTotal ?? order?.total ?? order?.netAmount, 0) }],
      subtotal: toNumber(order?.subTotal ?? order?.subtotal, 0),
      tax: toNumber(order?.totalTaxAmount ?? order?.taxAmount, 0),
      discount: toNumber(order?.totalDiscountAmount ?? order?.discountAmount, 0),
      total: toNumber(order?.grandTotal ?? order?.total ?? order?.netAmount, 0),
      itemsList: orderLines.map((line: any, index: number) => ({
        id: String(line?.id ?? line?.itemId ?? index + 1),
        name: line?.itemName ?? line?.name ?? line?.item?.name ?? 'Item',
        sku: line?.itemCode ?? line?.sku ?? line?.code ?? '-',
        quantity: toNumber(line?.quantity, 1),
        unitPrice: toNumber(line?.price ?? line?.unitPrice, 0),
        discount: toNumber(line?.discountItemAmount ?? line?.discountAmount, 0),
        vat: toNumber(
          line?.taxAmount ??
            line?.vatAmount ??
            line?.totalTaxAmount ??
            (Array.isArray(line?.taxes)
              ? line.taxes.reduce(
                  (sum: number, tax: any) =>
                    sum +
                    toNumber(
                      tax?.amount ??
                        tax?.taxAmount ??
                        tax?.value ??
                        tax?.tax?.amount,
                      0
                    ),
                  0
                )
              : 0),
          0
        ),
        total: toNumber(line?.amount ?? line?.subTotal ?? line?.total, 0),
      })),
    };
  };

  const loadRecentTransactions = async () => {
    setLoadingRecentTransactions(true);
    try {
      const counterSessionRaw = localStorage.getItem('counterSession');
      const counterSession = counterSessionRaw ? JSON.parse(counterSessionRaw) : null;
      const counterSessionId = Number(
        counterSession?.id ?? counterSession?.counterSessionId ?? counterSession?.sessionId ?? counterSession?.counterSession?.id ?? 0
      ) || null;

      const payload = {
        query: { start: 0, length: 5, search: { value: '' } },
        customerId: null,
        counterId: null,
        outletSessionId: null,
        counterSessionId,
        dateRange: null,
        fromDate: null,
        toDate: null,
      };

      const response = await posApi.getPosOrdersServerSide(payload);
      const data = unwrapResponse<any>(response);
      const orders = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setRecentTransactions(orders.map(mapOrderToTransaction));
    } catch {
      setRecentTransactions([]);
    } finally {
      setLoadingRecentTransactions(false);
    }
  };

  const visibleCustomers = useMemo(() => {
    const search = customerSearch.trim().toLowerCase();
    if (!search) return customerList;
    return customerList.filter((customer) => {
      const phone = customer.phoneNumber ?? '';
      return (
        customer.name?.toLowerCase().includes(search) ||
        phone.toLowerCase().includes(search)
      );
    });
  }, [customerList, customerSearch]);

  const handleCustomerCreated = async (customer: any) => {
    setShowCreateModal(false);
    const latestCustomers = await loadCustomers();
    const created = latestCustomers.find((c) => c.id === Number(customer?.id));
    onSelectCustomer(
      created ?? {
        id: Number(customer?.id),
        name: customer?.name ?? '',
        phoneNumber: customer?.phoneNumber ?? null,
        emailAddress: customer?.emailAddress ?? null,
      }
    );
  };

  const handleTransactionClick = async (txn: any) => {
    const orderId = toNumber(txn?.rawId, 0);
    if (!orderId) {
      setSelectedTransaction(txn);
      setShowTransactionDetails(true);
      return;
    }

    try {
      const response = await posApi.getPosOrderById(orderId);
      const details = unwrapResponse<any>(response);
      setSelectedTransaction(mapOrderToTransaction(details));
    } catch {
      setSelectedTransaction(txn);
    }
    setShowTransactionDetails(true);
  };

  const selectedLabel = selectedCustomer
    ? `${selectedCustomer.name}${selectedCustomer.phoneNumber ? ` (${selectedCustomer.phoneNumber})` : ''}`
    : 'Select Customer';

  return (
    <>
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <label className="block text-sm font-bold text-gray-900 mb-2 whitespace-nowrap">
            Select Customer <span className="text-red-600">*</span>
          </label>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              className="w-full px-3 py-2.5 border-2 border-sky-500 rounded-lg text-left text-sm bg-white flex items-center justify-between"
            >
              <span className={selectedCustomer ? 'text-slate-900' : 'text-slate-400'}>
                {selectedLabel}
              </span>
              <i className={`ri-arrow-${isDropdownOpen ? 'up' : 'down'}-s-line text-slate-500`} />
            </button>

            {isDropdownOpen && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-sky-300 rounded-lg shadow-lg overflow-hidden">
                <div className="p-2 border-b border-gray-200">
                  <input
                    autoFocus
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    placeholder="Search customer"
                  />
                </div>
                <div className="max-h-52 overflow-auto">
                  {isLoadingCustomers && (
                    <div className="px-3 py-2 text-sm text-gray-500">Loading customers...</div>
                  )}
                  {!isLoadingCustomers && visibleCustomers.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">No customer found</div>
                  )}
                  {!isLoadingCustomers &&
                    visibleCustomers.map((customer) => (
                      <button
                        type="button"
                        key={customer.id}
                        onClick={() => {
                          onSelectCustomer(customer);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-100 ${
                          selectedCustomer?.id === customer.id ? 'bg-slate-200' : ''
                        }`}
                      >
                        {customer.name}
                        {customer.phoneNumber ? ` (${customer.phoneNumber})` : ''}
                      </button>
                    ))}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsDropdownOpen(false);
                    setShowCreateModal(true);
                  }}
                  className="w-full px-3 py-2 border-t border-gray-200 text-left text-sm text-sky-700 font-semibold hover:bg-sky-50"
                >
                  Create New Customer
                </button>
              </div>
            )}
          </div>
          {customerError && (
            <p className="mt-2 text-xs text-red-600">{customerError}</p>
          )}
          {!allowWalkInCustomer && (
            <p className="mt-2 text-xs text-amber-700">Walk-in customer is disabled by POS settings.</p>
          )}
        </div>


        <SalesPersonPanel
          selectedSalesPerson={selectedSalesPerson}
          onSelectSalesPerson={onSelectSalesPerson}
          clearTrigger={clearSalesPersonTrigger}
        />

        <div className="flex-1 p-4 overflow-y-auto">
          <h3 className="text-xs font-bold text-gray-700 mb-3 whitespace-nowrap">Recent Transactions</h3>
          <div className="space-y-2">
            {loadingRecentTransactions && (
              <div className="text-xs text-gray-500 px-2 py-1">Loading recent transactions...</div>
            )}
            {recentTransactions.map((txn) => (
              <div
                key={txn.id}
                onClick={() => handleTransactionClick(txn)}
                className="p-2 bg-gray-50 hover:bg-gray-100 rounded cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">{txn.id}</span>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{txn.time}</span>
                </div>
                <div className="flex items-center justify-between">
                  {txn.discount > 0 ? (
                    <span className="text-xs text-red-500 whitespace-nowrap">Disc: -{Number(txn.discount).toFixed(2)}</span>
                  ) : <span />}
                  <span className="text-xs font-bold text-teal-600 whitespace-nowrap">{txn.amount}</span>
                </div>
              </div>
            ))}
            {!loadingRecentTransactions && recentTransactions.length === 0 && (
              <div className="text-xs text-gray-500 px-2 py-1">No recent transactions found.</div>
            )}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateCustomerModal
          onClose={() => setShowCreateModal(false)}
          onCustomerCreated={handleCustomerCreated}
        />
      )}

      {showTransactionDetails && selectedTransaction && (
        <TransactionDetailsModal
          onClose={() => setShowTransactionDetails(false)}
          transaction={selectedTransaction}
        />
      )}
    </>
  );
}

